import React, { useState } from 'react';
import { Row, Col, Modal, ModalHeader, ModalBody } from 'reactstrap';

interface MediaItem {
  id: string;
  file_name: string;
  file_type: string;
  file_size: number;
  mimetype: string;
  is_featured: boolean;
  property_id: string;
  property_unit_id: string;
  status: string;
  rejection_reason: string | null;
  createdAt: string;
  updatedAt: string;
  file_url: string; // Added file_url field
}

interface MediaData {
  images: MediaItem[];
  videos: MediaItem[];
  virtualTours: MediaItem[];
}

interface MediaProps {
  mediaData: MediaData;
}

const Media: React.FC<MediaProps> = ({ mediaData }) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [currentSection, setCurrentSection] = useState('');
  const [currentIndex, setCurrentIndex] = useState(0);
  const [currentMediaList, setCurrentMediaList] = useState<MediaItem[]>([]);
  
  const sectionData = [
    { label: 'Images', data: mediaData.images, type: 'images' },
    { label: 'Videos', data: mediaData.videos, type: 'videos' },
    { label: 'Virtual Tours', data: mediaData.virtualTours, type: 'virtualTours' }
  ];

  // Function to open modal with specific media type
  const openModal = (sectionType: string, mediaList: MediaItem[]) => {
    setCurrentSection(sectionType);
    setCurrentMediaList(mediaList);
    setCurrentIndex(0);
    setModalOpen(true);
  };

  // Navigation functions
  const goToNext = () => {
    if (currentIndex < currentMediaList.length - 1) {
      setCurrentIndex(currentIndex + 1);
    }
  };

  const goToPrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  };

  // Helper function to get media URL - use file_url for images/videos, file_name for virtual tours
  const getMediaUrl = (item: MediaItem) => {
    if (item.file_type === 'unit_virtual_tour') {
      return item.file_name; // Virtual tour URLs are in file_name (Matterport URLs)
    }
    return item.file_url; // For images and videos, use file_url (S3 URLs)
  };

  // Helper function to check if media has any content
  const hasAnyMedia = sectionData.some(section => section.data && section.data.length > 0);

  if (!hasAnyMedia) {
    return (
      <div className="rounded p-4 text-center mb-1">
        <span className="text-muted">No Media Available</span>
      </div>
    );
  }
  return (
    <div>
      {sectionData.map((section, idx) => {
        return section.data && section.data.length > 0 && (
          <div key={section.label} className={idx > 0 ? 'mt-4' : ''}>
            <div className="d-flex align-items-center mb-2">
              <span style={{ fontWeight: 400, fontSize: 15 }}>{section.label}</span>
              <span
                className="bg-light rounded-pill ms-2"
                style={{
                  fontSize: 16,
                  padding: '2px 10px',
                  fontWeight: 500,
                  display: 'inline-block'
                }}
              >
                {section.data.length}
              </span>
            </div>
            <Row>
              {section.data.map((item, _i) => (
                <Col
                  key={item.id}
                  xs={6}
                  sm={4}
                  md={3}
                  lg={2}
                  className="mb-3"
                >
                  <div
                    style={{
                      background: '#edeeee',
                      borderRadius: 4,
                      width: '100%',
                      aspectRatio: '1/1',
                      minHeight: 120,
                      overflow: 'hidden',
                      position: 'relative',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer', // Added cursor pointer for all media types
                      transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'scale(1.02)';
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'scale(1)';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                    onClick={() => {
                      openModal(section.type, section.data);
                    }}
                  >
                    {section.type === 'images' && (
                      <img
                        src={getMediaUrl(item)}
                        alt={item.file_name}
                        style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                        onError={e => { 
                          e.currentTarget.onerror = null; 
                          e.currentTarget.src = '/assets/images/placeholder-image.jpg'; 
                        }}
                      />
                    )}
                    {section.type === 'videos' && (
                      <div style={{ position: 'relative', width: '100%', height: '100%' }}>
                        <video
                          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                          muted
                          preload="metadata"
                        >
                          <source src={getMediaUrl(item)} type="video/mp4" />
                          Your browser does not support the video tag.
                        </video>
                        <div 
                          style={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            backgroundColor: 'rgba(0, 0, 0, 0.7)',
                            borderRadius: '50%', // Perfect circle
                            width: '50px', // Fixed width for perfect circle
                            height: '50px', // Fixed height for perfect circle
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          <i className="ri-play-fill" style={{ fontSize: '24px', color: 'white' }}></i>
                        </div>
                      </div>
                    )}
                    {section.type === 'virtualTours' && (
                      <div 
                        style={{ 
                          width: '100%', 
                          height: '100%', 
                          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'white',
                          padding: '20px',
                          textAlign: 'center',
                          position: 'relative'
                        }}
                      >
                        <i className="ri-vr-line" style={{ fontSize: '32px', marginBottom: '8px' }}></i>
                        <div style={{ fontSize: '12px', fontWeight: '500' }}>
                          Virtual Tour
                        </div>
                        <div style={{ fontSize: '10px', opacity: 0.8, marginTop: '4px' }}>
                          Click to explore
                        </div>
                        <div style={{ fontSize: '8px', marginTop: '4px', opacity: 0.6 }}>
                          {item.file_name}
                        </div>
                        <div 
                          style={{
                            position: 'absolute',
                            top: 8,
                            right: 8,
                            backgroundColor: 'rgba(255, 255, 255, 0.2)',
                            borderRadius: '50%',
                            padding: '4px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          <i className="ri-external-link-line" style={{ fontSize: '14px', color: 'white' }}></i>
                        </div>
                      </div>
                    )}
                    {item.is_featured && (
                      <div
                        style={{
                          position: 'absolute',
                          top: 8,
                          right: 8,
                          background: 'rgba(255, 193, 7, 0.9)',
                          color: 'white',
                          padding: '2px 6px',
                          borderRadius: 4,
                          fontSize: 10,
                          fontWeight: 600
                        }}
                      >
                        FEATURED
                      </div>
                    )}
                  </div>
                </Col>
              ))}
            </Row>
          </div>
        );
      })}
      
      {/* Modal for Media Viewer */}
      <Modal 
        isOpen={modalOpen} 
        toggle={() => setModalOpen(false)} 
        size={currentSection === 'virtualTours' ? 'xl' : 'lg'} 
        centered
      >
        <ModalHeader toggle={() => setModalOpen(false)}>
          {currentSection === 'images' ? 'Images' : currentSection === 'videos' ? 'Videos' : 'Virtual Tours'}
        </ModalHeader>
        <ModalBody>
          {currentMediaList.length > 0 && (
            <div>
              {/* Navigation Buttons */}
              <div className="d-flex justify-content-between align-items-center mb-3">
                <button 
                  className="btn btn-outline-secondary"
                  onClick={goToPrevious}
                  disabled={currentIndex === 0}
                >
                  <i className="ri-arrow-left-line"></i> Previous
                </button>
                <span className="text-muted">
                  {currentIndex + 1} of {currentMediaList.length}
                </span>
                <button 
                  className="btn btn-outline-secondary"
                  onClick={goToNext}
                  disabled={currentIndex === currentMediaList.length - 1}
                >
                  Next <i className="ri-arrow-right-line"></i>
                </button>
              </div>

              {/* Media Display */}
              <div style={{ minHeight: '400px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {currentSection === 'images' && currentMediaList[currentIndex] && (
                  <img
                    src={getMediaUrl(currentMediaList[currentIndex])}
                    alt={currentMediaList[currentIndex]?.file_name || 'Media'}
                    style={{ maxWidth: '100%', maxHeight: '400px', objectFit: 'contain' }}
                    onError={e => { 
                      e.currentTarget.onerror = null; 
                      e.currentTarget.src = '/assets/images/placeholder-image.jpg'; 
                    }}
                  />
                )}
                
                {currentSection === 'videos' && currentMediaList[currentIndex] && (
                  <video
                    src={getMediaUrl(currentMediaList[currentIndex])}
                    controls
                    style={{ maxWidth: '100%', maxHeight: '400px' }}
                  >
                    Your browser does not support the video tag.
                  </video>
                )}
                
                {currentSection === 'virtualTours' && currentMediaList[currentIndex] && (
                  <div style={{ width: '100%', height: '500px' }}>
                    <iframe
                      src={getMediaUrl(currentMediaList[currentIndex])}
                      style={{ width: '100%', height: '100%', border: 'none', borderRadius: '8px' }}
                      allow="autoplay; fullscreen; web-share; xr-spatial-tracking;"
                      allowFullScreen
                      title="Virtual Tour"
                    ></iframe>
                  </div>
                )}
              </div>

              {/* Thumbnail Navigation */}
              <div className="mt-3">
                <Row>
                  {currentMediaList.map((item, index) => (
                    <Col key={item.id} xs={2} className="mb-2">
                      <div
                        style={{
                          border: index === currentIndex ? '2px solid #007bff' : '1px solid #dee2e6',
                          borderRadius: '4px',
                          padding: '2px',
                          cursor: 'pointer',
                          height: '60px',
                          overflow: 'hidden',
                          position: 'relative'
                        }}
                        onClick={() => setCurrentIndex(index)}
                      >
                        {currentSection === 'images' ? (
                          <img
                            src={getMediaUrl(item)}
                            alt={item.file_name}
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            onError={e => { 
                              e.currentTarget.onerror = null; 
                              e.currentTarget.src = '/assets/images/placeholder-image.jpg'; 
                            }}
                          />
                        ) : currentSection === 'videos' ? (
                          <div style={{ position: 'relative', width: '100%', height: '100%' }}>
                            <video
                              src={getMediaUrl(item)}
                              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                              muted
                              preload="metadata"
                            />
                            <div 
                              style={{
                                position: 'absolute',
                                top: '50%',
                                left: '50%',
                                transform: 'translate(-50%, -50%)',
                                backgroundColor: 'rgba(0, 0, 0, 0.6)',
                                borderRadius: '50%',
                                width: '20px',
                                height: '20px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}
                            >
                              <i className="ri-play-fill" style={{ fontSize: '10px', color: 'white' }}></i>
                            </div>
                          </div>
                        ) : (
                          <div 
                            style={{ 
                              width: '100%', 
                              height: '100%', 
                              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: 'white'
                            }}
                          >
                            <i className="ri-vr-line" style={{ fontSize: '16px', marginBottom: '2px' }}></i>
                            <div style={{ fontSize: '8px', textAlign: 'center' }}>VR</div>
                          </div>
                        )}
                      </div>
                    </Col>
                  ))}
                </Row>
              </div>
            </div>
          )}
        </ModalBody>
      </Modal>
    </div>
  );
};

export default Media;